/**
 * Lobby seat pieces shared by the lobby (host and non-host) and the
 * join-by-code screen: one seat row and the small action pill ("Color",
 * "Invite", "Add bot ▾", "Take this seat", "Resend", "✕").
 */

import React from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native'
import { PLAYER_COLORS, PlayerColorChip, clampPlayerSlot } from '@warcarp/ui'
import type { TeamId, VacationResponse } from '@warcarp/api-types'
import { TeamShape } from '../TeamShape'
import { PlayerVacationIndicator } from '../../settings/PlayerVacationIndicator'
import { useTheme } from '../../../hooks/useTheme'
import { playerChipRim } from '../../../lib/playerColor'

export function MiniAction({ label, accessibilityLabel, onPress, disabled, loading, tone = 'default', testID }: {
  label: string
  accessibilityLabel: string
  onPress: () => void
  disabled?: boolean
  loading?: boolean
  /** 'primary' = the seat's main call to action (Take this seat). */
  tone?: 'default' | 'primary' | 'quiet'
  testID?: string
}) {
  const box = tone === 'primary'
    ? 'bg-blood border border-blood'
    : tone === 'quiet' ? 'border border-transparent' : 'bg-paper border border-olive'
  const text = tone === 'primary' ? 'text-paper' : tone === 'quiet' ? 'text-ink-soft' : 'text-ink'
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled || loading}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled: !!(disabled || loading), busy: !!loading }}
      testID={testID}
      className={`${box} rounded-full px-3 flex-row items-center justify-center gap-1 ${disabled ? 'opacity-50' : ''}`}
      style={{ minHeight: 32, minWidth: 32 }}
      hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
    >
      {loading && <ActivityIndicator size="small" />}
      <Text className={`${text} font-stencil text-[11px] uppercase tracking-wider`} numberOfLines={1}>{label}</Text>
    </TouchableOpacity>
  )
}

export type SeatRowKind = 'filled' | 'invited' | 'open'

/** The open / invited seat's dashed outline colour. */
const OPEN_SEAT_OUTLINE = '#6b7d3a'
/** The chip size (PlayerColorChip `sm`). */
const SEAT_CHIP_SIZE = 20

/**
 * One seat. Filled seats show the player's colour; invited seats a dashed
 * outline (no colour yet); open seats a hollow dot. The viewer's own seat is
 * outlined and tagged "· you". In a Pick seats team lobby every seat belongs
 * to a team (`team`), and its chip or outline takes the team's shape.
 */
export function LobbySeatRow({ slot, kind, name, meta, colorSlot, team, you, vacation, children, testID }: {
  slot: number
  kind: SeatRowKind
  name: string
  meta?: string
  /** Palette slot for a filled seat. */
  colorSlot?: number
  /** Pick seats team lobbies: the seat's team, drawn as its shape. Null keeps the circle. */
  team?: TeamId | null
  you?: boolean
  vacation?: VacationResponse | null
  /** The seat's actions (MiniAction pills). */
  children?: React.ReactNode
  testID?: string
}) {
  const border = you
    ? 'border-2 border-olive-up'
    : kind === 'filled' ? 'border border-olive/40' : 'border border-olive/60'
  // Dark colours (Raven) vanish on dark seats: a light rim in dark mode.
  const { theme } = useTheme()
  const chipRim = playerChipRim(theme)
  const label = `Seat ${slot}: ${kind === 'open' ? 'open' : name}${you ? ', you' : ''}${meta ? `, ${meta}` : ''}`
  return (
    <View
      testID={testID ?? `lobby-seat-${slot}`}
      accessibilityLabel={label}
      className={`flex-row items-center gap-2.5 rounded-xl px-3 py-2 ${kind === 'filled' ? 'bg-paper-up' : 'bg-paper'} ${border}`}
      style={{ minHeight: 52, ...(kind !== 'filled' && !you ? { borderStyle: 'dashed' } : null) }}
    >
      {team ? (
        kind === 'filled' && colorSlot != null ? (
          // The player's colour in the team's shape, with the chip's rim
          // (light in dark mode, else the chip's faint dark edge).
          <TeamShape team={team} size={SEAT_CHIP_SIZE} fill={PLAYER_COLORS[clampPlayerSlot(colorSlot)].hex}
            stroke={chipRim?.borderColor ?? 'rgba(0,0,0,0.15)'} strokeWidth={chipRim?.borderWidth ?? 1}
            testID={`lobby-seat-${slot}-shape`} />
        ) : (
          <TeamShape team={team} size={SEAT_CHIP_SIZE} fill="none" stroke={OPEN_SEAT_OUTLINE} strokeWidth={1.5}
            strokeDasharray="3 2" testID={`lobby-seat-${slot}-shape`} />
        )
      ) : kind === 'filled' && colorSlot != null ? (
        <PlayerColorChip slot={clampPlayerSlot(colorSlot)} size="sm" style={chipRim} />
      ) : (
        <View style={{ width: 20, height: 20, borderRadius: 10, borderWidth: 1.5, borderStyle: 'dashed', borderColor: OPEN_SEAT_OUTLINE }} />
      )}
      <View style={{ flex: 1, minWidth: 0 }}>
        <View className="flex-row items-center" style={{ gap: 6, minWidth: 0 }}>
          <Text
            style={{ flexShrink: 1 }}
            numberOfLines={1}
            className={`font-typed text-sm ${kind === 'open' ? 'text-ink-soft italic' : 'text-ink'}`}
          >
            {name}
          </Text>
          {you && <Text className="text-olive-up font-typed text-xs">· you</Text>}
          {vacation !== undefined && <PlayerVacationIndicator name={name} vacation={vacation} />}
        </View>
        {!!meta && <Text numberOfLines={1} className="text-ink-soft font-typed text-xs">{meta}</Text>}
      </View>
      {children ? <View className="flex-row items-center" style={{ gap: 6, flexShrink: 0 }}>{children}</View> : null}
    </View>
  )
}
